/**
 * The Resources page FAQ. Lives here (not inline in the page component) so
 * both the rendered accordion (client/src/pages/Resources.tsx) and the
 * FAQPage JSON-LD (shared/seoMetadata.ts) read the exact same content --
 * schema must never describe something different from what's visibly on
 * the page.
 */
import { TIPS_SHORT_NAME, WARRANTY_CREW_CAB_BRUSH_TRUCK, WARRANTY_LIFETIME_INSTALLS } from "./companyFacts";

export interface FaqEntry {
  q: string;
  a: string;
}

export const RESOURCES_FAQ: FaqEntry[] = [
  {
    q: "How long does a typical vehicle upfit take?",
    a: "Once all parts are on-hand, most law enforcement upfits are completed within 10 business days. Complex builds (K9 conversions, fire command vehicles) may take 15–20 days. We'll give you a firm timeline at the quote stage.",
  },
  {
    q: "Do you work with agencies outside of Texas?",
    a: `Yes. While we're based in Dickinson, TX, we work with agencies across the Gulf Coast region and can accommodate out-of-state deliveries. Our ${TIPS_SHORT_NAME} and federal contracts are available to eligible agencies nationwide.`,
  },
  {
    q: "What warranty do you offer?",
    a: `We provide a ${WARRANTY_LIFETIME_INSTALLS} on all installations we perform directly. Parts are covered by the respective manufacturer warranties (Federal Signal, Havis, Setina, etc.). One exception: the 2027 Crew Cab Brush Truck is manufactured by Redline (we're the authorized dealer), so it carries Redline's ${WARRANTY_CREW_CAB_BRUSH_TRUCK.term} instead.`,
  },
  {
    q: "Can I purchase through a cooperative contract?",
    a: `Yes. We are active on ${TIPS_SHORT_NAME} cooperative contracts, which allow eligible Texas and Gulf Coast agencies to purchase without a competitive bid process. Contact us and we'll confirm your eligibility.`,
  },
  {
    q: "Do you supply the vehicle or just the upfitting?",
    a: "We primarily provide upfitting services — you supply the vehicle or we can coordinate with your fleet dealer. We work with all major OEM fleet programs and can advise on vehicle spec for your intended build.",
  },
  {
    q: "What brands do you install?",
    a: "We are factory-authorized installers for Federal Signal, Havis, Setina, Whelen, Motorola, and other leading emergency equipment manufacturers. We can also accommodate agency-specified brands.",
  },
  {
    q: "Can you handle fleet orders?",
    a: "Absolutely. We regularly handle fleet orders of 5–50+ vehicles for agencies and commercial fleets. Fleet orders receive priority scheduling and standardized build documentation for your records.",
  },
  {
    q: "Are you registered in SAM.gov?",
    a: "Yes. Texas First Response Services is fully registered and active in SAM.gov with an active CAGE code. We hold six SBA certifications including WOSB, EDWOSB, SDVOSB, VOSB, HUBZone, and 8(a).",
  },
];

/**
 * The Municipal Fleet Vehicle Protection page's government-buyer FAQ. Lives
 * here (not inline in the page component) for the same reason as
 * RESOURCES_FAQ: it feeds both the rendered, fully-visible Q&A section
 * (client/src/pages/MunicipalFleetVehicleProtection.tsx) and the FAQPage
 * JSON-LD (shared/seoMetadata.ts) from one source, so schema never describes
 * something the page doesn't actually show. Originally written against the
 * maintainer-supplied research brief (docs/engineering/backlog/GH-258-
 * municipal-fleet-vehicle-protection-research-brief.md), which flagged
 * claims-backing details (obligor/administrator/insurer) as unresolved and
 * left the last answer deflecting to "ask your rep."
 *
 * 2026-09-29: that detail is now sourced — MUNICIPAL_QUICK_SHEET_FINAL.pdf
 * and a maintainer-supplied messaging/TAM reference doc both name real
 * administrators and insurers per program (Vital Trends: Total 360 /
 * biBerk Insurance Services, a Berkshire Hathaway subsidiary, A++ AM Best;
 * Apex: Ascent Administration / Old Republic, A+ AM Best) — the last
 * answer below now states them directly. AM Best ratings were NOT
 * independently re-verified against ambest.com from here (no live network
 * access in this sandbox) — they're stated as given in that source
 * material, per its own "verify AM Best ratings are current before
 * publishing" note. Re-confirm before this goes live if it's been a while.
 */
/**
 * The TFRSupply launch-event page's FAQ (client/src/pages/FederalSignalLaunch.tsx).
 * Lives here for the same reason as the other FAQ arrays: it feeds both the
 * rendered accordion and the FAQPage JSON-LD in shared/seoMetadata.ts from one
 * source. Every answer is used verbatim as specified by the maintainer brief
 * for this page — do not rewrite or shorten any of it.
 *
 * One deliberate exception, per an AEO review: the "Who can take part" and
 * "When does the wholesale pricing apply" answers were each a few words,
 * too thin to stand alone as an extractable answer for an AI system that
 * only pulls this one Q&A. Expanded both using facts already stated
 * elsewhere on this same page (the hero paragraph and "The Launch Event"
 * section) — nothing new was introduced.
 */
export const TFRSUPPLY_LAUNCH_FAQ: FaqEntry[] = [
  {
    q: "What is TFRSupply?",
    a: "TFRSupply is an e-commerce platform built for Texas first responders. Agencies plan their current and future builds in agency profiles and order parts for their vehicles.",
  },
  {
    q: "Who can take part in the launch event?",
    a: "Every agency in Texas can take part. Taking part means sharing basic information about the agency and its fleet — estimates are fine, and agencies that already keep fleet records can share them as is.",
  },
  {
    q: "When does the wholesale pricing apply?",
    a: "Wholesale pricing on Federal Signal equipment applies to an agency's first purchase on TFRSupply. It's offered as part of the TFRSupply launch event, open to every agency in Texas.",
  },
  {
    q: "What does wholesale pricing mean here?",
    a: "Wholesale describes the pricing on Federal Signal equipment offered as part of the launch event. TFRSupply itself is an e-commerce platform for agencies.",
  },
  {
    q: "What is in an agency's profile and workspace?",
    a: "The workspace is where an agency sees its active builds, fleet builds, and projects. Planning tools include fleet builds, department standards, fleet templates, and a project summary. The profile and its planning tools are free.",
  },
  {
    q: "How does sharing fleet information help?",
    a: "TFRSupply uses it to build out the agency's profile with the agency's own fleet, and to understand how Texas agencies manage their fleets.",
  },
  {
    q: "What information does TFRSupply need?",
    a: "Basic information about the agency and its fleet: Year/Make/Model, approximate mileage, and replacement cycles. Estimates are fine.",
  },
  {
    q: "We already track our fleet. Can we send those records?",
    a: "Yes. Agencies that already track their fleet in a spreadsheet, software, or logs can share those records as is.",
  },
  {
    q: "Do we need to know the vehicle first?",
    a: "No. Every item offered is either universal or has its mounting hardware sold separately, so equipment can be procured before the vehicle is known. Mounting hardware can be procured once fitment is confirmed.",
  },
  {
    q: "Who do we contact with questions?",
    a: "Call +1 281-843-3528 or email admin@tfrsupply.com.",
  },
];

/**
 * The /supply-store account-claim page's FAQ. A curated subset of
 * TFRSUPPLY_LAUNCH_FAQ's answers above — reused verbatim where they apply
 * to claiming an account generally, leaving out the Federal-Signal launch
 * event's fleet-data/wholesale-pricing-specific questions, which don't
 * apply here. The two new entries below describe only what the claim form
 * and its submission mechanically do (see TfrSupplyClaimForm.tsx and
 * server/highlevel.ts's submitAgencyClaimLead) — not a cost, launch date,
 * or other fact this codebase doesn't otherwise state.
 */
export const TFRSUPPLY_CLAIM_FAQ: FaqEntry[] = [
  {
    q: "What is TFRSupply?",
    a: "TFRSupply is an e-commerce platform built for Texas first responders. Agencies plan their current and future builds in agency profiles and order parts for their vehicles.",
  },
  {
    q: "What is in an agency's profile and workspace?",
    a: "The workspace is where an agency sees its active builds, fleet builds, and projects. Planning tools include fleet builds, department standards, fleet templates, and a project summary. The profile and its planning tools are free.",
  },
  {
    q: "What happens after I submit the claim form?",
    a: "TFRSupply uses the submitted contact and agency information to set up the agency's account ahead of launch. The email address you provide is used for your store login.",
  },
  {
    q: "Who do we contact with questions?",
    a: "Call +1 281-843-3528 or email admin@tfrsupply.com.",
  },
];

export const FLEET_PROTECTION_FAQ: FaqEntry[] = [
  {
    q: "Is Municipal Fleet Vehicle Protection insurance?",
    a: "No. It's a vehicle service contract (VSC), a legal category separate from insurance under most state laws. A VSC provider agrees to pay for or reimburse covered repairs after your manufacturer's warranty ends — but it isn't an insurance policy, and your agency's existing insurance program is unaffected.",
  },
  {
    q: "Who pays for a covered repair, and how fast?",
    a: "Your shop calls the plan administrator for prior authorization before starting a covered repair. Once approved, payment goes directly to the repair facility rather than requiring your agency to pay up front and wait for reimbursement — keeping the vehicle back in service with minimal cash-flow disruption.",
  },
  {
    q: "Can our own mechanics still do the work?",
    a: "Yes. Fleet service is allowed under this program, so departments that run their own maintenance shops can continue doing covered repairs in-house instead of being routed to an outside shop — a flexibility many consumer vehicle service contracts don't offer.",
  },
  {
    q: "Is upfitted equipment like lights, sirens, and pumps covered?",
    a: "Upfit equipment — emergency lighting, sirens, plows, winches, fire pumps and aerials, and stretcher power-load systems — can be included, unlike most generic vehicle service contracts, which typically cover only the base vehicle. Confirm which specific equipment is written into your contract before enrolling.",
  },
  {
    q: "Are older, already in-service vehicles eligible?",
    a: "Yes. Emergency Vehicle Protection covers vehicles up to 30 model years, and Commercial Vehicle Protection up to 20 — new purchases and vehicles already in your fleet are both eligible, so you don't have to wait for your next replacement cycle to get coverage.",
  },
  {
    q: "Is coverage renewable and transferable?",
    a: "Yes — coverage is renewable at term end and transferable if a covered vehicle is reassigned or sold within your fleet, so protection isn't lost to routine fleet reallocation. Ask your program consultant for the specific renewal and transfer terms in your contract.",
  },
  {
    q: "Who backs these contracts if a claim needs to be paid?",
    a: "Emergency Vehicle Protection is administered by Total 360 and insured by biBerk Insurance Services, a Berkshire Hathaway subsidiary (A++ rated by AM Best). Commercial Vehicle Protection is administered by Ascent Administration and insured by Old Republic (A+ rated by AM Best). Ask your Texas First Response Services representative for a sample contract and the current obligor details before you enroll any vehicles.",
  },
];
